/*
 * PMX search — one live-suggestion behaviour, shared by every PAWMART search box.
 *
 * Two surfaces use this: the passage search on the homepage and the global
 * header search. They were written separately, and the second one immediately
 * fell behind the first — no catalogue fallback, no spelling correction, an
 * empty panel where a message belonged. One copy, one set of fixes.
 *
 * The escalation, in order, and each step only runs because the one before it
 * came back empty:
 *
 *   1. Shopify Predictive Search, rendered server-side through a section. Fast,
 *      supported, and the right engine for as-you-type. It matches titles,
 *      vendors, types and tags, so it cannot read a sentence.
 *   2. The storefront catalogue endpoint, which matches on intent and product
 *      descriptions. "something to stop my puppy chewing the furniture" finds
 *      the Coffee Wood Chew here and nowhere else.
 *   3. Spelling correction. Shopify's typo tolerance needs the first four
 *      letters correct, so a transposition like "chwe" defeats both engines.
 *      Rather than ship a dictionary, the store's own search becomes one.
 *   4. An honest empty state. A hidden panel reads as a broken feature.
 *
 * Every caller's form stays a real GET to the search route, so with no
 * JavaScript, a failed request, or a throttled response, the box still works.
 */
(() => {
  const DEBOUNCE_MS = 200;
  const MIN_QUERY = 2;
  const MIN_SEMANTIC = 3;
  // 3, not 4: "chw" is a dropped-letter typo for "chew" and has to qualify.
  const MIN_CORRECTION_WORD = 3;
  const MAX_CANDIDATES = 10;
  const BATCH_SIZE = 5;
  const VOWELS = ['e', 'a', 'o', 'i', 'u'];
  const RESULT_LIMIT = 4;

  const attach = (config) => {
    const {
      form,
      input,
      panel,
      status,
      predictiveEndpoint,
      predictiveSection,
      semanticEndpoint,
      semanticLabel = 'Suggested for you',
      resourceTypes = 'product,collection',
      // What counts as "inside" for dismissal. The header renders its panel as
      // a sibling of the form, so testing the form alone would close the panel
      // the moment a result took focus. Defaults to the form for callers that
      // nest the panel within it.
      root = form,
    } = config;

    if (!form || !input || !panel || !predictiveEndpoint || !predictiveSection) {
      return { close: () => {}, destroy: () => {} };
    }

    const listeners = new AbortController();
    const { signal } = listeners;
    let timer = 0;
    let request = null;
    let active = -1;

    const links = () => [...panel.querySelectorAll('[role="option"] a')];

    const close = () => {
      active = -1;
      panel.hidden = true;
      panel.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
    };

    const move = (delta) => {
      const options = links();
      if (!options.length) return;
      const next = active + delta;
      if (next < 0) {
        active = -1;
        options.forEach((link) => link.parentElement.setAttribute('aria-selected', 'false'));
        input.focus();
        return;
      }
      active = next % options.length;
      options.forEach((link, index) => {
        link.parentElement.setAttribute('aria-selected', String(index === active));
      });
      options[active].focus();
    };

    const reveal = () => {
      if (matchMedia('(min-width: 768px)').matches) return;
      requestAnimationFrame(() => {
        const rect = panel.getBoundingClientRect();
        const overflow = rect.bottom - (innerHeight - 96);
        if (overflow <= 0) return;
        const top = Math.min(overflow, Math.max(0, rect.top - 16));
        scrollBy({
          top,
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      });
    };

    const open = () => {
      active = -1;
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      reveal();
      if (!status) return;
      const count = links().length;
      status.textContent = count
        ? `${count} suggestion${count === 1 ? '' : 's'} available.`
        : 'No suggestions found.';
    };

    const currency = (amount, code) => {
      if (typeof amount !== 'number' || Number.isNaN(amount)) return '';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: code || 'CAD',
          // narrowSymbol so these read "$14.99" like the money filter beside
          // them, not "CA$14.99" — two price formats in one panel looks broken.
          currencyDisplay: 'narrowSymbol',
        }).format(amount);
      } catch (_error) {
        return `${amount.toFixed(2)} ${code || ''}`.trim();
      }
    };

    // The endpoints answer in different shapes. Normalise both to the four
    // things a suggestion row shows, so there is one renderer.
    const fromCatalog = (product) => {
      const media = product.media?.find((entry) => entry.type === 'image')
        || product.variants?.[0]?.media?.find((entry) => entry.type === 'image');
      const min = product.price_range?.min;
      return {
        title: product.title,
        url: product.url,
        image: media?.url,
        // The catalogue quotes minor units; suggest.json quotes major.
        price: currency(typeof min?.amount === 'number' ? min.amount / 100 : NaN, min?.currency),
      };
    };

    const fromSuggest = (product) => ({
      title: product.title,
      url: product.url,
      image: product.featured_image?.url || product.image,
      price: currency(Number(product.price), undefined),
    });

    const viewAll = (query) => {
      const all = document.createElement('a');
      all.className = 'pmx-predictive-search__all';
      all.href = `${form.getAttribute('action')}?q=${encodeURIComponent(query)}`;
      all.textContent = 'View all results ↗';
      return all;
    };

    const renderRows = (items, labelText, query) => {
      const group = document.createElement('div');
      group.className = 'pmx-search__group';

      const label = document.createElement('p');
      label.className = 'pmx-search__label';
      label.textContent = labelText;
      group.append(label);

      const list = document.createElement('ul');
      list.setAttribute('role', 'listbox');
      list.setAttribute('aria-label', labelText);

      items.forEach((item) => {
        // Same-origin destinations only. Anything else is not our catalogue.
        let url;
        try {
          url = new URL(item.url, location.origin);
        } catch (_error) {
          return;
        }
        if (url.origin !== location.origin) return;

        const option = document.createElement('li');
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');

        const link = document.createElement('a');
        link.href = url.href;

        const thumb = document.createElement('span');
        if (item.image) {
          const image = document.createElement('img');
          image.src = item.image;
          image.alt = '';
          image.loading = 'lazy';
          image.width = 48;
          image.height = 48;
          thumb.append(image);
        }

        const title = document.createElement('b');
        title.textContent = item.title || '';

        const price = document.createElement('small');
        price.textContent = item.price || '';

        link.append(thumb, title, price);
        option.append(link);
        list.append(option);
      });

      if (!list.children.length) return false;

      group.append(list, viewAll(query));
      panel.append(group);
      return true;
    };

    const requestSemantic = async (query) => {
      if (!semanticEndpoint || query.length < MIN_SEMANTIC) return false;
      try {
        const response = await fetch(semanticEndpoint, {
          method: 'POST',
          signal: request.signal,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            id: 1,
            method: 'tools/call',
            params: {
              name: 'search_catalog',
              arguments: {
                meta: { 'ucp-agent': { profile: location.origin } },
                catalog: { query, pagination: { limit: RESULT_LIMIT } },
              },
            },
          }),
        });
        if (!response.ok) return false;
        const payload = await response.json();
        const text = payload?.result?.content?.[0]?.text;
        if (!text) return false;
        const products = JSON.parse(text)?.products;
        if (!Array.isArray(products) || !products.length) return false;
        if (!renderRows(products.slice(0, RESULT_LIMIT).map(fromCatalog), semanticLabel, query)) return false;
        open();
        return true;
      } catch (error) {
        if (error.name !== 'AbortError') close();
        return false;
      }
    };

    const transpositions = (word) => {
      const out = [];
      for (let index = 0; index < word.length - 1 && out.length < MAX_CANDIDATES; index += 1) {
        const letters = [...word];
        [letters[index], letters[index + 1]] = [letters[index + 1], letters[index]];
        const candidate = letters.join('');
        if (candidate !== word) out.push(candidate);
      }
      return out;
    };

    const suggestJson = async (query) => {
      const parameters = new URLSearchParams({
        q: query,
        'resources[type]': 'product',
        'resources[limit]': String(RESULT_LIMIT),
      });
      const response = await fetch(`${predictiveEndpoint}.json?${parameters}`, {
        signal: request.signal,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Suggest failed: ${response.status}`);
      const payload = await response.json();
      return payload?.resources?.results?.products || [];
    };

    /*
     * Candidates cover the three single-edit typos people actually make, in the
     * order they are worth spending a request on:
     *
     *   transposed  "chwe" → chew
     *   dropped     "chw"  → chew     (vowel-major: e before a before o…)
     *   doubled     "cheww" → chew
     *
     * Vowel-major ordering matters. Position-major would put "chew" sixth for
     * the query "chw", behind five candidates beginning "ce…"; vowel-major puts
     * it second. Consonants are not inserted — a dropped consonant is rarer and
     * would multiply the candidate list past what one interaction can pay for.
     */
    const candidatesFor = (word) => {
      const seen = new Set([word]);
      const take = (list, limit) => {
        const out = [];
        for (const candidate of list) {
          if (out.length >= limit) break;
          if (seen.has(candidate)) continue;
          seen.add(candidate);
          out.push(candidate);
        }
        return out;
      };

      const dropped = [];
      for (const vowel of VOWELS) {
        for (let index = 1; index < word.length; index += 1) {
          dropped.push(word.slice(0, index) + vowel + word.slice(index));
        }
      }

      // Deletions that collapse a repeated letter come first. Left-to-right
      // order would spend the whole quota on "heww"/"ceww" before reaching the
      // one that matters, "chew".
      const repeated = [];
      const rest = [];
      for (let index = 0; index < word.length; index += 1) {
        const candidate = word.slice(0, index) + word.slice(index + 1);
        const isRepeat = word[index] === word[index - 1] || word[index] === word[index + 1];
        (isRepeat ? repeated : rest).push(candidate);
      }
      const doubled = [...repeated, ...rest].filter((candidate) => candidate.length >= 3);

      return [
        ...take(transpositions(word), 3),
        ...take(dropped, 4),
        ...take(doubled, 3),
      ].slice(0, MAX_CANDIDATES);
    };

    const requestCorrection = async (query) => {
      const words = query.split(/\s+/u).filter(Boolean);
      const target = words.reduce((longest, word) => (word.length > longest.length ? word : longest), '');
      if (target.length < MIN_CORRECTION_WORD) return false;

      const candidates = candidatesFor(target.toLowerCase());
      if (!candidates.length) return false;

      try {
        // Batched rather than all at once: the first batch usually wins, and
        // firing every candidate in parallel is a good way to get throttled.
        for (let start = 0; start < candidates.length; start += BATCH_SIZE) {
          const batch = candidates.slice(start, start + BATCH_SIZE);
          const attempts = await Promise.all(batch.map(async (candidate) => {
            const corrected = words.map((word) => (word === target ? candidate : word)).join(' ');
            try {
              return { corrected, candidate, products: await suggestJson(corrected) };
            } catch (_error) {
              return { corrected, candidate, products: [] };
            }
          }));
          const winner = attempts.find((attempt) => attempt.products.length);
          if (!winner) continue;
          const label = `Showing results for “${winner.candidate}”`;
          if (!renderRows(winner.products.slice(0, RESULT_LIMIT).map(fromSuggest), label, winner.corrected)) return false;
          open();
          return true;
        }
        return false;
      } catch (error) {
        if (error.name !== 'AbortError') close();
        return false;
      }
    };

    // Never leave a typed query answered by silence.
    const renderEmpty = (query) => {
      const group = document.createElement('div');
      group.className = 'pmx-search__group';
      const label = document.createElement('p');
      label.className = 'pmx-search__label';
      label.textContent = `No matches for “${query}”`;
      group.append(label, viewAll(query));
      panel.append(group);
      open();
    };

    const requestSuggestions = async (query) => {
      request?.abort();
      request = new AbortController();
      const parameters = new URLSearchParams({
        q: query,
        'resources[type]': resourceTypes,
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,vendor,tag',
        section_id: predictiveSection,
      });
      form.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${predictiveEndpoint}?${parameters}`, {
          signal: request.signal,
          headers: { Accept: 'text/html' },
        });
        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const rendered = parsed.querySelector(`#shopify-section-${predictiveSection}`);
        panel.innerHTML = rendered ? rendered.innerHTML : '';
        active = -1;
        const count = links().length;
        if (count > 0) {
          open();
        } else {
          panel.hidden = true;
          input.setAttribute('aria-expanded', 'false');
          if (status) status.textContent = 'No suggestions found.';
          // Drop the "0 results" shell, then escalate.
          panel.innerHTML = '';
          const answered = await requestSemantic(query) || await requestCorrection(query);
          if (!answered) renderEmpty(query);
        }
      } catch (error) {
        if (error.name !== 'AbortError') close();
      } finally {
        form.removeAttribute('aria-busy');
      }
    };

    input.addEventListener('input', () => {
      clearTimeout(timer);
      const query = input.value.trim();
      if (query.length < MIN_QUERY) {
        request?.abort();
        close();
        return;
      }
      timer = setTimeout(() => requestSuggestions(query), DEBOUNCE_MS);
    }, { signal });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' && !panel.hidden) {
        event.preventDefault();
        move(1);
      } else if (event.key === 'Escape') {
        close();
      }
    }, { signal });

    panel.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        move(1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        move(-1);
      } else if (event.key === 'Escape') {
        close();
        input.focus();
      }
    }, { signal });

    document.addEventListener('pointerdown', (event) => {
      if (!root.contains(event.target)) close();
    }, { signal });

    root.addEventListener('focusout', () => {
      setTimeout(() => {
        if (!root.contains(document.activeElement)) close();
      }, 0);
    }, { signal });

    return {
      close,
      destroy() {
        clearTimeout(timer);
        request?.abort();
        listeners.abort();
      },
    };
  };

  window.PMXSearch = { attach };
})();
